import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { ButtonComponent } from '@core/ui-kit/components';
import { TranslocoPipe } from '@jsverse/transloco';
import { DialogCloseDirective, DialogRef } from '@ngneat/dialog';
import { AuthStore } from '@shared/data-access/data-access-authentication';
import {
  PaymentMetadata,
  PaymentScope,
  Product,
  SUBSCRIPTION_PURCHASE_SUCCESS_KEY,
} from '@shared/data-access/data-access-payment';
import { TokenStore } from '@shared/data-access/data-access-token';

import { SubscriptionPlanCardComponent } from './subscription-plan-card/subscription-plan-card.component';

@Component({
  selector: 'app-subscription-plans-dialog',
  imports: [TranslocoPipe, SubscriptionPlanCardComponent, DialogCloseDirective, NgOptimizedImage, ButtonComponent],
  templateUrl: './subscription-plans-dialog.component.html',
  styleUrl: './subscription-plans-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubscriptionPlansDialogComponent {
  private readonly authStore = inject(AuthStore);
  private readonly tokenStore = inject(TokenStore);
  private readonly ref = inject(DialogRef);

  products = this.ref.data['products'];
  cyberExposureProducts = this.ref.data['cyberExposureProducts'];

  canManageSubscription = this.authStore.canManageSubscription;
  isFinance = this.authStore.isFinance;

  // Set by the payment-handler after a successful subscription checkout, read once
  // here when the dialog reopens on /payment so we can confirm the purchase.
  purchaseSuccess = signal<boolean>(this.readAndClearPurchaseSuccess());

  private readAndClearPurchaseSuccess(): boolean {
    const hasSuccess = sessionStorage.getItem(SUBSCRIPTION_PURCHASE_SUCCESS_KEY) !== null;
    if (hasSuccess) {
      sessionStorage.removeItem(SUBSCRIPTION_PURCHASE_SUCCESS_KEY);
    }
    return hasSuccess;
  }

  logout(): void {
    this.authStore.logout();
  }

  createCheckout({
    product,
    cyberExposureProductId,
  }: {
    product: Product;
    cyberExposureProductId: string | null;
  }): void {
    const metadata = {
      [PaymentMetadata.PLAN_TYPE]: product.name?.toLocaleLowerCase(),
      [PaymentMetadata.IMAGE]: product.images?.[0],
    };

    const products = [
      {
        priceId: product.defaultPrice.id,
        quantity: 1,
      },
    ];

    if (cyberExposureProductId) {
      products.push({
        priceId: cyberExposureProductId,
        quantity: 1,
      });
    }

    this.tokenStore.createCheckoutSession({
      scope: PaymentScope.PLAN,
      checkoutItems: products,
      metadata,
    });
  }
}
